import { makeStyles, withStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

import { EeCheckbox } from 'components/EeCheckbox/EeCheckbox';

import { SectionMap, SectionType } from '../../../../../dashboardSettingsTypes';
import { useSettingsRow } from './useSettingsRow';

const useStyles = makeStyles({
  root: {
    width: '100%',
    marginBottom: '7.8px',
    borderRadius: '4px',
    boxShadow: 'none',
    minHeight: '0px',
    '&.Mui-expanded': {
      minHeight: 'auto',
    }
  },
  draggingBackground: {
    backgroundColor: '#f4f6f8',
    border: 'none',
    boxShadow: 'none',
  },
  summary: {
    margin: '0px !important',
  },
});

const StyledAccordingSummary = withStyles({
  root: {
    margin: '0px',
    height: '40px',
    backgroundColor: '#f4f6f8',
    borderRadius: '4px',
  },
  content: {
    margin: '0px !important',
  },
})(AccordionSummary);

export function SettingsRow(
  {
    initialSection,
    sectionMap,
    updateSection,
  }: {
    initialSection: SectionType,
    sectionMap: SectionMap,
    updateSection: CallableFunction,
  }) {
  const classes = useStyles();
  const logic = useSettingsRow(initialSection, updateSection);

  function getItemStyle(isDragging: boolean, draggableStyle: any) {
    return {
      // some basic styles to make the items look a bit nicer
      userSelect: 'none',
      width: '448px',
      height: '32px',
      margin: '2.8px 0 4px 3px',
      borderRadius: '4px',
      border: 'solid 1px #e6e6e6',
      backgroundColor: '#ffffff',

      // change background colour if dragging
      background: isDragging ? 'grey' : 'white',

      // styles we need to apply on draggables
      ...draggableStyle,
    }
  }

  return (
    <div className={classes.root}>
      <Accordion
        TransitionProps={{ unmountOnExit: true }}
        className={classes.draggingBackground}
        classes={{
          root: `lens-according`, // class name, e.g. `classes-nesting-root-x`
        }}
      >
        <StyledAccordingSummary
          expandIcon={logic.section.components.length ? <ExpandMoreIcon /> : null}
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          classes={{
            root: `${classes.root} lens-according-summary`, // class name, e.g. `classes-nesting-root-x`
          }}
          aria-label="Expand"
          aria-controls="additional-actions1-content"
          id="additional-actions1-header"
        >
          <EeCheckbox
            label={sectionMap.label}
            onClick={(event: any) => event.stopPropagation()}
            onFocus={(event: any) => event.stopPropagation()}
            checked={
              logic.section.components.length
                ? !logic.section.components.every((c) => !c.checked)
                : logic.section.checked
            }
            indeterminate={
              !logic.section.components.every((c) => !c.checked) &&
              logic.section.components.some((c) => !c.checked)
            }
            onChange={(e) => logic.updateSectionFlag(e.target.checked)}
          />
        </StyledAccordingSummary>
        {!!logic.section.components.length && (
          <AccordionDetails className="collapsed-row">
            <DragDropContext onDragEnd={logic.onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided: any) => (
                  <div
                    className="w-100 d-flex flex-column align-items-center"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ padding: 2, width: 250 }}
                  >
                    {logic.section.components.map((item, index) => (
                      <Draggable
                        key={item.name}
                        draggableId={item.name}
                        index={index}
                      >
                        {(provided: any, snapshot: any) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style,
                            )}
                            className="lens-draggable"
                          >
                            <div className="d-flex justify-content-between p-1 component lens-draggable-section">
                              <EeCheckbox
                                label={sectionMap.components[item.name].label}
                                checked={logic.section.components.find((c) => c.name === item.name)?.checked}
                                onChange={(e) =>
                                  logic.updateComponentFlag(item.name, e.target.checked)
                                }
                              />
                              <DragIndicatorIcon />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </AccordionDetails>
        )}
      </Accordion>
    </div>
  );
}
